import { useEffect, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Backdrop, Panel, Header, Footer, Field } from "./DepositModal";

export default function TransferModal({ initialAsset, onClose }) {
  const [assets, setAssets] = useState(null);
  const [balances, setBalances] = useState({});
  const [asset, setAsset] = useState(initialAsset || null);
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/api/wallet/assets"),
      api.get("/api/wallet/balances"),
    ]).then(([a, b]) => {
      setAssets(a.data.assets);
      const map = {};
      (b.data.balances || []).forEach((bal) => { map[bal.asset] = bal; });
      setBalances(map);
      if (!asset && a.data.assets.length) {
        setAsset(initialAsset || a.data.assets[0].symbol);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bal = balances[asset];
  const insufficient = bal && Number(amount || 0) > Number(bal.available);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/api/wallet/internal-transfer", {
        asset, amount, recipient_email: recipient.trim().toLowerCase(),
        note: note || undefined,
      });
      setSuccess(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <Backdrop onClose={() => onClose(true)}>
        <Panel>
          <Header title="Transfer Sent" onClose={() => onClose(true)} testId="transfer-close" />
          <div className="p-6 space-y-3 text-[13px]">
            <div className="rounded-lg bg-up/10 text-up p-4">
              <div className="font-semibold">Sent {amount} {asset} to {recipient}</div>
              <div className="text-[12px] mt-1">Reference: <b className="font-mono">{success.tx_id}</b></div>
              <div className="text-[12px]">Internal transfers are instant — no fees, no on-chain confirmation needed.</div>
            </div>
          </div>
          <Footer>
            <button
              onClick={() => onClose(true)}
              className="px-5 py-2 rounded-lg brand-gradient text-primary-foreground text-[13px] font-semibold"
              data-testid="transfer-success-done"
            >
              Done
            </button>
          </Footer>
        </Panel>
      </Backdrop>
    );
  }

  return (
    <Backdrop onClose={() => onClose(false)}>
      <Panel>
        <Header title="Send to a PaisaCX User" onClose={() => onClose(false)} testId="transfer-close" />
        <div className="p-6 space-y-4">
          <div className="rounded-lg bg-primary/10 text-primary p-3 text-[12px]">
            Internal transfers are instant and zero-fee. Both you and the recipient must be KYC-approved.
          </div>

          <Field label="Recipient Email">
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="recipient@email.com"
              className="w-full px-3 py-2.5 rounded-lg border border-border text-[14px]"
              data-testid="transfer-recipient"
            />
          </Field>

          <Field label="Asset">
            <select
              value={asset || ""}
              onChange={(e) => setAsset(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary text-[14px]"
              data-testid="transfer-asset"
            >
              {(assets || []).map((a) => (
                <option key={a.symbol} value={a.symbol}>{a.symbol} — {a.name}</option>
              ))}
            </select>
          </Field>

          <Field label={
            <span className="flex items-center justify-between">
              <span>Amount</span>
              <span className="font-normal normal-case text-[11px] text-muted-foreground">
                Available: {bal ? Number(bal.available).toFixed(6) : "0"} {asset}
              </span>
            </span>
          }>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2.5 rounded-lg border border-border text-[14px]"
              data-testid="transfer-amount"
            />
          </Field>

          <Field label="Note (optional)">
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What's this for?"
              className="w-full px-3 py-2.5 rounded-lg border border-border text-[13px]"
              data-testid="transfer-note"
              maxLength={120}
            />
          </Field>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-down/10 text-down text-[12px]">
              <AlertCircle size={14} className="mt-0.5" /> {error}
            </div>
          )}
        </div>
        <Footer>
          <button
            onClick={() => onClose(false)}
            className="px-4 py-2 rounded-lg border border-border text-foreground text-[13px] font-semibold"
            data-testid="transfer-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !recipient || !amount || insufficient}
            className="px-5 py-2 rounded-lg brand-gradient text-primary-foreground text-[13px] font-semibold disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="transfer-submit"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Send
          </button>
        </Footer>
      </Panel>
    </Backdrop>
  );
}
